import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Spin } from 'antd';
import { Search, PlusCircle, MinusCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import MobileSheetBase from './MobileSheetBase';

const MobileSelectSheet = ({
  isOpen,
  onClose,
  title,
  options = [],
  value,
  onChange,
  loading = false,
  onSearch,
  onLoadMore,
  hasMore = false,
  optionRender,
  showAllToggle,
  placeholder,
}) => {
  const { t } = useTranslation('riskCalculator');
  const [searchValue, setSearchValue] = useState('');
  const contentRef = useRef(null);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      setSearchValue('');
    }
  }, [isOpen]);

  const debouncedSearch = useCallback(
    debounce((term) => {
      if (onSearch) {
        onSearch(term);
      }
    }, 300),
    [onSearch]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearchChange = useCallback((e) => {
    const term = e.target.value;
    setSearchValue(term);
    debouncedSearch(term);
  }, [debouncedSearch]);

  const handleSelect = useCallback((optionValue, option) => {
    onChange(optionValue, option);
    onClose();
  }, [onChange, onClose]);

  const handleScroll = useCallback((e) => {
    if (!onLoadMore || !hasMore || loading || loadingMoreRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const scrollThreshold = 50;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - scrollThreshold;

    if (isNearBottom) {
      loadingMoreRef.current = true;
      onLoadMore();
      setTimeout(() => {
        loadingMoreRef.current = false;
      }, 500);
    }
  }, [onLoadMore, hasMore, loading]);

  const displayOptions = onSearch
    ? options
    : options.filter((opt) => {
        if (!searchValue) return true;
        const label = String(opt.label || '').toLowerCase();
        const name = String(opt.name || '').toLowerCase();
        const searchLower = searchValue.toLowerCase();
        return label.includes(searchLower) || name.includes(searchLower);
      });

  const renderOptionContent = useCallback((option) => {
    if (optionRender) {
      return optionRender(option);
    }
    return (
      <div className="flex-1 min-w-0">
        <div className="text-xs text-gray-900">{option.label}</div>
      </div>
    );
  }, [optionRender]);

  return (
    <MobileSheetBase
      isOpen={isOpen}
      onClose={onClose}
      title={title}
    >
      {/* Search */}
      <div className="sticky top-0 px-3 py-2 bg-white border-b border-gray-100 z-10 flex-shrink-0">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="text"
            className="w-full h-10 pl-9 pr-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-[#207d54]"
            placeholder={placeholder || t('search_placeholder', 'Rechercher...')}
            value={searchValue}
            onChange={handleSearchChange}
          />
        </div>
      </div>

      {/* Content */}
      <div
        className="flex-1 overflow-y-auto overscroll-contain"
        ref={contentRef}
        onScroll={handleScroll}
      >
        {/* Loading state - always show when loading */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Spin size="default" />
          </div>
        )}

        {/* Empty state */}
        {!loading && displayOptions.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400 text-center">
            <div className="text-xs">
              {t('cascading_no_result', 'Aucun résultat trouvé')}
            </div>
          </div>
        )}

        {/* Options list */}
        {!loading && displayOptions.length > 0 && (
          <div className="py-1">
            {displayOptions.map((option) => {
              const isSelected = option.value === value;

              return (
                <div
                  key={option.value}
                  className={`flex items-center min-h-[44px] px-3 py-2 cursor-pointer ${
                    isSelected ? 'bg-green-50' : ''
                  }`}
                  onClick={() => handleSelect(option.value, option)}
                >
                  <div
                    className={`w-4 h-4 border-2 rounded-full mr-3 flex-shrink-0 flex items-center justify-center ${
                      isSelected ? 'border-[#207d54]' : 'border-gray-300'
                    }`}
                  >
                    {isSelected && <div className="w-2 h-2 bg-[#207d54] rounded-full" />}
                  </div>
                  {renderOptionContent(option)}
                </div>
              );
            })}
          </div>
        )}

        {/* Loading more */}
        {loading && displayOptions.length > 0 && (
          <div className="flex items-center justify-center py-4">
            <Spin size="small" />
          </div>
        )}
      </div>

      {/* Toggle */}
      {showAllToggle?.enabled && (
        <div
          className="flex items-center gap-2 px-3 py-2 border-t border-gray-100 bg-gray-50 cursor-pointer text-[#105235] text-xs font-medium flex-shrink-0"
          onClick={showAllToggle.onClick}
        >
          {showAllToggle.active ? (
            <MinusCircle size={14} />
          ) : (
            <PlusCircle size={14} />
          )}
          <span>{showAllToggle.label}</span>
        </div>
      )}
    </MobileSheetBase>
  );
};

MobileSelectSheet.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  onSearch: PropTypes.func,
  onLoadMore: PropTypes.func,
  hasMore: PropTypes.bool,
  optionRender: PropTypes.func,
  showAllToggle: PropTypes.shape({
    enabled: PropTypes.bool,
    label: PropTypes.string,
    active: PropTypes.bool,
    onClick: PropTypes.func,
  }),
  placeholder: PropTypes.string,
};

export default React.memo(MobileSelectSheet);
